import { useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowLeft, Instagram, Linkedin } from "lucide-react";
import BackButton from "@/components/BackButton";
import PageHeader from "@/components/PageHeader";
import { cleanPolicyText } from "@/utils/policyText";

const PROMISE = [
  "Premium Quality",
  "Honest Pricing",
  "Timeless Design",
  "Gender-Neutral Fits",
  "Built For Everyday Wear",
  "Made With Purpose",
];

const CHAPTERS = [
  {
    n: "01",
    kicker: "Our Story",
    title: "A Quiet Rebellion Against Excess.",
    body:
      "Every great brand begins with a vision. The Black House was created to bridge the gap between premium fashion and everyday affordability. We believe that clothing should inspire confidence through quality — not through excessive branding. Every collection reflects our commitment to minimal design, durable construction and honest value.",
  },
  {
    n: "02",
    kicker: "Our Collections",
    title: "Two Lines. One Language.",
    body:
      "Our collections are divided into two lines. The Studio Collection represents elevated craftsmanship — premium fabrics, refined detailing, and the kind of pieces that quietly announce themselves. The Essential Collection delivers everyday oversized essentials designed for comfort, versatility and long-term wear.",
  },
  {
    n: "03",
    kicker: "What Makes Us Different",
    title: "Not A Trend. A Wardrobe.",
    body:
      "We design gender-neutral apparel with modern oversized fits. Each launch follows a curated drop approach with coordinated colour palettes, allowing customers to build a cohesive wardrobe over time. We prioritise quality materials, clean aesthetics and consistent fit over fast-changing trends.",
  },
  {
    n: "04",
    kicker: "Our Community",
    title: "More Than Apparel.",
    body:
      "The Black House is more than clothing. We are building a community that values simplicity, confidence and thoughtful design. We welcome feedback, celebrate creativity, and strive to create products our customers will enjoy wearing for years.",
  },
  {
    n: "05",
    kicker: "Looking Ahead",
    title: "The House Grows Slowly.",
    body:
      "Our journey begins with premium oversized T-shirts and will continue to expand into polos, shirts, activewear and accessories — while staying true to our minimalist design philosophy. Each addition earns its place; nothing is added simply to fill the shelf.",
  },
];

export default function About() {
  useEffect(() => {
    document.title = "About — THE BLACK HOUSE";
    window.scrollTo(0, 0);
  }, []);

  const ease = [0.16, 1, 0.3, 1];

  return (
    <main
      data-testid="about-page"
      className="relative bg-black text-white overflow-x-hidden"
    >
      <PageHeader />
      <BackButton />
      {/* Corner meta */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 0.3 }}
        className="absolute top-36 left-5 md:top-40 md:left-10 z-10 font-mono-ui text-[9px] md:text-[10px] tracking-[0.35em] text-[#5a5a5a] uppercase"
      >
        About · Volume I
      </motion.div>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 0.3 }}
        className="absolute top-36 right-5 md:top-40 md:right-10 z-10 font-mono-ui text-[9px] md:text-[10px] tracking-[0.35em] text-[#5a5a5a] uppercase text-right"
      >
        Est. MMXXVI · Faridabad, IN
      </motion.div>

      {/* Hero */}
      <header className="max-w-5xl mx-auto px-6 md:px-10 pt-40 md:pt-44 pb-8 md:pb-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.1, ease }}
          className="flex flex-col items-center text-center"
        >
          <p className="font-mono-ui text-[10px] tracking-[0.4em] uppercase text-[#6a6a6a] mt-0">
            — About Us
          </p>
          <p data-testid="about-brand-summary" className="mt-3 font-mono-ui text-[10px] md:text-[11px] tracking-[0.28em] uppercase text-white">
            Premium Unisex Streetwear in India
          </p>
          <h1
            data-testid="about-title"
            className="font-display font-bold text-white uppercase mt-4 text-[11vw] sm:text-[7vw] md:text-[4.6vw] leading-[0.95]"
            style={{ letterSpacing: "0.18em" }}
          >
            Built To Last.
            <br />
            Designed For Every Day.
          </h1>
          <div className="w-16 h-px bg-white/60 mt-8" />
          <p className="font-serif-editorial italic text-[#c9c9c4] max-w-2xl mt-6 leading-[1.7] text-[17px] md:text-[20px]">
            {cleanPolicyText("The Black House was founded with a simple belief: premium unisex streetwear should balance craftsmanship, comfort and design. We create a complete wardrobe range — from oversized fits and graphic tees to everyday basics, seasonal layers and accessories — with clean silhouettes, thoughtful details and lasting quality.")}
          </p>
        </motion.div>
      </header>

      {/* Chapters */}
      <section className="max-w-5xl mx-auto px-6 md:px-10 pb-8 md:pb-10">
        <div className="border-t border-[#141414]">
          {CHAPTERS.map((c, i) => (
            <motion.article
              key={c.n}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-10%" }}
              transition={{ duration: 1, ease }}
              className="grid grid-cols-12 gap-6 md:gap-10 py-10 md:py-14 border-b border-[#141414] items-start"
              data-testid={`about-chapter-${c.n}`}
            >
              <div className="col-span-12 md:col-span-3">
                <p className="font-mono-ui text-[11px] tracking-[0.3em] text-[#7a7a7a]">
                  Ch. {c.n}
                </p>
                <p className="font-mono-ui text-[9px] tracking-[0.35em] uppercase text-[#5a5a5a] mt-2">
                  {c.kicker}
                </p>
              </div>
              <div className={`col-span-12 md:col-span-9 ${i % 2 === 1 ? "md:pl-12" : ""}`}>
                <h2
                  className="font-serif-editorial italic text-white text-[2rem] sm:text-[2.4rem] md:text-[3.4rem] leading-[1.05]"
                  style={{ letterSpacing: "-0.015em" }}
                >
                  {c.title}
                </h2>
                <p className="mt-6 md:mt-8 font-mono-ui text-[11px] md:text-[12px] tracking-[0.15em] uppercase text-[#a8a8a2] max-w-xl leading-[2]">
                  {cleanPolicyText(c.body)}
                </p>
              </div>
            </motion.article>
          ))}
        </div>
      </section>

      {/* Promise grid */}
      <section className="max-w-5xl mx-auto px-6 md:px-10 pb-8 md:pb-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-8%" }}
          transition={{ duration: 1, ease }}
          className="flex items-baseline justify-between mb-10 md:mb-14"
        >
          <p className="font-mono-ui text-[10px] tracking-[0.35em] uppercase text-[#5a5a5a]">
            — Our Promise
          </p>
          <p className="font-mono-ui text-[10px] tracking-[0.35em] uppercase text-[#5a5a5a]">
            {PROMISE.length} Tenets
          </p>
        </motion.div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-px bg-[#141414]">
          {PROMISE.map((p, i) => (
            <motion.div
              key={p}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-10%" }}
              transition={{ duration: 0.7, delay: i * 0.05, ease }}
              className="bg-black p-6 md:p-10 flex flex-col justify-between min-h-[130px] md:min-h-[180px]"
              data-testid={`promise-${i + 1}`}
            >
              <span className="font-mono-ui text-[10px] tracking-[0.35em] uppercase text-[#5a5a5a]">
                {String(i + 1).padStart(2, "0")}
              </span>
              <span
                className="font-display font-bold uppercase text-white text-[15px] md:text-[19px] leading-[1.15] mt-4"
                style={{ letterSpacing: "0.14em" }}
              >
                {cleanPolicyText(p)}
              </span>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Closing quote */}
      <section className="max-w-4xl mx-auto px-6 md:px-10 pb-8 md:pb-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-10%" }}
          transition={{ duration: 1, ease }}
          className="border-t border-[#141414] pt-10 md:pt-12 text-center"
        >
          <p className="font-serif-editorial italic text-white text-2xl md:text-4xl leading-[1.2] max-w-3xl mx-auto">
            "Elegance is what remains when the noise is removed."
          </p>
          <p className="font-mono-ui text-[10px] tracking-[0.35em] uppercase text-[#5a5a5a] mt-6">
            — House Diary
          </p>
        </motion.div>
      </section>

      {/* Contact strip */}
      <section className="max-w-4xl mx-auto px-6 md:px-10 pb-0">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-8%" }}
          transition={{ duration: 0.9, ease }}
          className="grid grid-cols-12 gap-4 md:gap-8 py-6 md:py-8 border-t border-[#141414]"
        >
          <div className="col-span-12 md:col-span-2">
            <span className="font-mono-ui text-[11px] md:text-[12px] tracking-[0.3em] text-[#7a7a7a]">
              Contact
            </span>
          </div>
          <div className="col-span-12 md:col-span-10 space-y-3 font-mono-ui text-[11px] md:text-[12px] tracking-[0.25em] uppercase text-[#c9c9c4]">
            <p>
              Website:{" "}
              <a
                href="https://www.theblackhouse.in"
                className="text-white hover:underline underline-offset-4"
              >
                theblackhouse.in
              </a>
            </p>
            <p>
              Email:{" "}
              <a
                href="mailto:support@theblackhouse.in"
                className="text-white hover:underline underline-offset-4"
              >
                support@theblackhouse.in
              </a>
            </p>
            <p>Location: Faridabad, Haryana, India</p>
          </div>
        </motion.div>
      </section>

      {/* Footer */}
      <footer className="border-t border-[#141414]">
        <div className="max-w-5xl mx-auto px-6 md:px-10 py-4 md:py-5 flex flex-col md:flex-row items-center justify-between gap-6">
          <Link
            data-testid="about-back-home"
            to="/"
            className="group inline-flex items-center gap-2 font-mono-ui text-[10px] md:text-[11px] tracking-[0.3em] uppercase text-[#8a8a8a] hover:text-white transition-colors duration-500"
          >
            <ArrowLeft
              size={13}
              strokeWidth={1.4}
              className="transition-transform duration-500 group-hover:-translate-x-1"
            />
            Return To The House
          </Link>
          <div className="flex items-center gap-3 flex-wrap justify-center">
            <Link to="/terms" className="font-mono-ui text-[10px] md:text-[11px] tracking-[0.3em] uppercase text-[#8a8a8a] hover:text-white transition-colors duration-500">Terms</Link>
            <span className="text-[#2a2a2a]">·</span>
            <Link to="/privacy" className="font-mono-ui text-[10px] md:text-[11px] tracking-[0.3em] uppercase text-[#8a8a8a] hover:text-white transition-colors duration-500">Privacy</Link>
            <span className="text-[#2a2a2a]">·</span>
            <Link to="/accessibility" className="font-mono-ui text-[10px] md:text-[11px] tracking-[0.3em] uppercase text-[#8a8a8a] hover:text-white transition-colors duration-500">Accessibility</Link>
            <span className="text-[#2a2a2a]">·</span>
            <Link to="/security" className="font-mono-ui text-[10px] md:text-[11px] tracking-[0.3em] uppercase text-[#8a8a8a] hover:text-white transition-colors duration-500">Security</Link>
            <span className="text-[#2a2a2a]">·</span>
            <a href="https://www.instagram.com/theblackhouse.tbh" target="_blank" rel="noreferrer noopener" aria-label="Instagram" className="group w-9 h-9 flex items-center justify-center border border-[#1a1a1a] hover:border-white/70 transition-colors duration-500">
              <Instagram size={13} strokeWidth={1.4} className="text-white/70 group-hover:text-white transition-colors duration-500" />
            </a>
            <a href="https://www.linkedin.com/company/the-black-house/" target="_blank" rel="noreferrer noopener" aria-label="LinkedIn" className="group w-9 h-9 flex items-center justify-center border border-[#1a1a1a] hover:border-white/70 transition-colors duration-500">
              <Linkedin size={13} strokeWidth={1.4} className="text-white/70 group-hover:text-white transition-colors duration-500" />
            </a>
          </div>
        </div>
        <div className="border-t border-[#141414] px-6 md:px-10 py-3 flex items-center justify-between font-mono-ui text-[8px] md:text-[9px] tracking-[0.3em] uppercase text-[#3a3a3a]">
          <span>© MMXXVI — THE BLACK HOUSE</span>
          <span>All rights are gathered.</span>
        </div>
      </footer>
    </main>
  );
}
